import type { ComponentType } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconTextSize,
  IconFileText,
  IconMapPin,
  IconMap,
  IconClock,
  IconAlertTriangle,
  IconShape,
  IconPalette,
  IconPhoto,
  IconCheck,
  IconCheckbox,
  IconList,
  IconPaperclip,
  IconMessage,
  IconUser,
  IconCalendar,
  IconLink,
} from '@tabler/icons-react';

import type {
  OperationHistoryConfig,
  OperationHistoryEvent,
  OperationHistoryEventDiff,
  OperationEventColor,
} from '@/components/common/OperationHistory';
import type { TaskHistoryEvent } from '@/types';
import { TaskHistoryEventType } from '@/types';
import { lowerFirst } from '@/utils/helpers';

const TASK_EVENT_ICONS: Record<
  TaskHistoryEventType,
  ComponentType<{ size?: number }>
> = {
  [TaskHistoryEventType.TaskCreated]: IconPlus,
  [TaskHistoryEventType.TaskUpdated]: IconEdit,
  [TaskHistoryEventType.TaskDeleted]: IconTrash,
  [TaskHistoryEventType.TitleChanged]: IconTextSize,
  [TaskHistoryEventType.DescriptionChanged]: IconFileText,
  [TaskHistoryEventType.LocationChanged]: IconMapPin,
  [TaskHistoryEventType.StatusChanged]: IconClock,
  [TaskHistoryEventType.PriorityChanged]: IconAlertTriangle,
  [TaskHistoryEventType.MarkerShapeChanged]: IconShape,
  [TaskHistoryEventType.MarkerColorChanged]: IconPalette,
  [TaskHistoryEventType.MarkerIconChanged]: IconPhoto,
  [TaskHistoryEventType.RequirementCompleted]: IconCheck,
  [TaskHistoryEventType.RequirementUncompleted]: IconCheckbox,
  [TaskHistoryEventType.RequirementUpdated]: IconCheckbox,
  [TaskHistoryEventType.RequirementAdded]: IconList,
  [TaskHistoryEventType.RequirementDeleted]: IconTrash,
  [TaskHistoryEventType.AttachmentAdded]: IconPaperclip,
  [TaskHistoryEventType.AttachmentDeleted]: IconPaperclip,
  [TaskHistoryEventType.CommentAdded]: IconMessage,
  [TaskHistoryEventType.CommentUpdated]: IconMessage,
  [TaskHistoryEventType.CommentDeleted]: IconMessage,
  [TaskHistoryEventType.AssigneeChanged]: IconUser,
  [TaskHistoryEventType.DueDateChanged]: IconCalendar,
  [TaskHistoryEventType.StartDateChanged]: IconCalendar,
  [TaskHistoryEventType.RelatedTaskAdded]: IconLink,
  [TaskHistoryEventType.RelatedTaskRemoved]: IconLink,
  [TaskHistoryEventType.RequireRelatedTasksChanged]: IconLink,
  [TaskHistoryEventType.MapRepresentationChanged]: IconMap,
};

const TASK_EVENT_COLORS: Record<TaskHistoryEventType, OperationEventColor> = {
  // GREEN - Creation / Addition
  [TaskHistoryEventType.TaskCreated]: 'green',
  [TaskHistoryEventType.RequirementAdded]: 'green',
  [TaskHistoryEventType.AttachmentAdded]: 'green',
  [TaskHistoryEventType.CommentAdded]: 'green',
  [TaskHistoryEventType.RelatedTaskAdded]: 'green',
  [TaskHistoryEventType.RequirementCompleted]: 'green',

  // ORANGE - Uncomplete (was 'orange.6')
  [TaskHistoryEventType.RequirementUncompleted]: 'orange',

  // RED - Deletion / Removal
  [TaskHistoryEventType.TaskDeleted]: 'red',
  [TaskHistoryEventType.RequirementDeleted]: 'red',
  [TaskHistoryEventType.AttachmentDeleted]: 'red',
  [TaskHistoryEventType.CommentDeleted]: 'red',
  [TaskHistoryEventType.RelatedTaskRemoved]: 'red',

  // INDIGO - Content / Text Changes
  [TaskHistoryEventType.TaskUpdated]: 'indigo',
  [TaskHistoryEventType.TitleChanged]: 'indigo',
  [TaskHistoryEventType.DescriptionChanged]: 'indigo',
  [TaskHistoryEventType.RequirementUpdated]: 'indigo',
  [TaskHistoryEventType.CommentUpdated]: 'indigo',

  // PURPLE - Visual / Appearance / Location
  [TaskHistoryEventType.MarkerShapeChanged]: 'purple',
  [TaskHistoryEventType.MarkerColorChanged]: 'purple',
  [TaskHistoryEventType.MarkerIconChanged]: 'purple',
  [TaskHistoryEventType.LocationChanged]: 'purple',
  [TaskHistoryEventType.MapRepresentationChanged]: 'purple',

  // ORANGE - Workflow / Status Changes
  [TaskHistoryEventType.StatusChanged]: 'orange',
  [TaskHistoryEventType.PriorityChanged]: 'orange',
  [TaskHistoryEventType.AssigneeChanged]: 'orange',
  [TaskHistoryEventType.DueDateChanged]: 'orange',
  [TaskHistoryEventType.StartDateChanged]: 'orange',
  [TaskHistoryEventType.RequireRelatedTasksChanged]: 'orange',
};

export function mapTaskEvent(event: TaskHistoryEvent): OperationHistoryEvent {
  return {
    id: event.id,
    eventType: event.eventType,
    description: event.description,
    authorName: event.author,
    authorId: event.authorId,
    authorAvatarUrl: event.authorAvatarUrl ?? null,
    createdAt: event.createdAt,
    metadata: event.metadata as Record<string, unknown> | undefined,
  };
}

const VALUE_TRANSLATION_PREFIX: Partial<Record<TaskHistoryEventType, string>> =
  {
    [TaskHistoryEventType.StatusChanged]: 'taskStatus',
    [TaskHistoryEventType.PriorityChanged]: 'priority',
  };

function isKnownTaskEventType(type: string): type is TaskHistoryEventType {
  return type in TASK_EVENT_ICONS;
}

export function useTaskHistoryConfig(): OperationHistoryConfig {
  const { t } = useTranslation();

  return useMemo(() => {
    function translateValue(eventType: string, value: string): string {
      const prefix =
        VALUE_TRANSLATION_PREFIX[eventType as TaskHistoryEventType];
      if (prefix) return t(`${prefix}.${value}`, { defaultValue: value });
      return value;
    }

    function extractEventDiff(
      event: OperationHistoryEvent
    ): OperationHistoryEventDiff | null {
      if (!event.metadata) return null;
      const meta = event.metadata;
      const reqText = (meta.requirementText ?? meta.text) as string | undefined;

      // Requirement completion: show requirement name (green for completed, red for uncompleted)
      if (
        event.eventType === TaskHistoryEventType.RequirementCompleted ||
        event.eventType === TaskHistoryEventType.RequirementUncompleted
      ) {
        if (!reqText) return null;
        return event.eventType === TaskHistoryEventType.RequirementCompleted
          ? { to: String(reqText) }
          : { from: String(reqText) };
      }

      // Standard old -> new value changes (translate enums)
      if (meta.oldValue !== undefined && meta.newValue !== undefined) {
        return {
          from: translateValue(event.eventType, String(meta.oldValue)),
          to: translateValue(event.eventType, String(meta.newValue)),
        };
      }

      // Requirement text changes
      if (meta.oldText !== undefined && meta.newText !== undefined) {
        return { from: String(meta.oldText), to: String(meta.newText) };
      }

      // Requirement order changes
      if (meta.oldOrder !== undefined && meta.newOrder !== undefined) {
        return { from: String(meta.oldOrder), to: String(meta.newOrder) };
      }

      // Addition-only (requirement text, attachment name, comment preview)
      if (reqText) return { to: String(reqText) };
      if (meta.attachmentName) return { to: String(meta.attachmentName) };
      if (meta.commentPreview) return { to: String(meta.commentPreview) };

      // Related task
      if (meta.relatedTaskTitle) return { to: String(meta.relatedTaskTitle) };

      return null;
    }

    function getInterpolationParams(
      event: OperationHistoryEvent
    ): Record<string, string> {
      const meta = event.metadata;
      if (!meta) return {};

      const reqText = (meta.requirementText ?? meta.text) as string | undefined;

      return {
        text: reqText != null ? String(reqText) : '',
        title:
          meta.relatedTaskTitle != null ? String(meta.relatedTaskTitle) : '',
      };
    }

    return {
      getEventIcon: (event) =>
        isKnownTaskEventType(event.eventType)
          ? TASK_EVENT_ICONS[event.eventType]
          : IconEdit,

      getEventColor: (event) =>
        isKnownTaskEventType(event.eventType)
          ? TASK_EVENT_COLORS[event.eventType]
          : 'gray',

      getEventSummary: (event) =>
        t(`history.events.${lowerFirst(event.eventType)}`, {
          defaultValue: event.description,
          ...getInterpolationParams(event),
        }),

      getEventDiff: extractEventDiff,

      getEventTypeLabel: (eventType) =>
        t(`history.eventTypeLabels.${lowerFirst(eventType)}`, {
          defaultValue: eventType,
        }),

      allEventTypes: Object.values(TaskHistoryEventType) as readonly string[],
    };
  }, [t]);
}
